/**
 * Eine Zeile der Zählerliste (Spec 46a, „Zeile"): Symbol der Art · Label mit
 * Haus darunter · rechts letzter Stand und Statuszeile. Die ganze Zeile ist
 * der Link auf das Detail (`?zaehler=`).
 *
 * Alle Texte kommen vom Server (`label`, `house.label`, `value_display`,
 * `unit`, `status_display`). Gefärbt wird ausschließlich anhand von
 * `is_reading_due` — das Wort „Ablesung fällig" steht in der Statuszeile, die
 * Farbe (Punkt und 3-px-Rand) ist nie das einzige Signal. Das Symbol je Art ist
 * ein Bezeichnerabgleich (`kind.value` → Lucide-Symbol), kein Fachwert.
 */

import { Link } from '@tanstack/react-router'
import { Droplet, Flame, Fuel, Thermometer, Zap } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { MeterRow as MeterRowData } from '@/features/energy/api'

const KIND_ICONS: Record<string, LucideIcon> = {
  electricity: Zap,
  gas: Flame,
  water: Droplet,
  heat: Thermometer,
  oil_tank: Fuel,
}

interface MeterRowProps {
  readonly meter: MeterRowData
  /** `haus` der URL — bleibt beim Öffnen des Details erhalten. */
  readonly house: string | undefined
  readonly isSelected: boolean
}

export function MeterRow({ meter, house, isSelected }: MeterRowProps) {
  const Icon = KIND_ICONS[meter.kind.value] ?? Zap
  const edge = meter.is_reading_due ? 'border-l-status-partial' : isSelected ? 'border-l-accent' : 'border-l-transparent'

  return (
    <li>
      <Link
        to="/energie"
        search={{ haus: house, zaehler: meter.id }}
        data-testid={`energy-meter-row-${meter.id}`}
        aria-current={isSelected ? 'true' : undefined}
        className={`border-border flex min-h-(--row-h) items-center gap-3 border-b border-l-[3px] px-(--page-x) py-2 transition-colors duration-(--duration-fast) ease-(--ease-out) ${edge} ${
          isSelected ? 'bg-bg' : 'hover:bg-bg'
        }`}
      >
        <Icon aria-hidden="true" className="text-muted size-4 shrink-0" />

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-body text-text truncate font-semibold">{meter.label}</span>
          {meter.house !== null && <span className="text-label text-muted truncate">{meter.house.label}</span>}
        </span>

        <span className="flex shrink-0 flex-col items-end">
          {meter.last_reading !== null && (
            <span data-numeric data-testid={`energy-meter-value-${meter.id}`} className="text-body text-text">
              {meter.last_reading.value_display} {meter.unit}
            </span>
          )}
          <span
            data-testid={`energy-meter-status-${meter.id}`}
            className={`text-label flex items-center gap-1.5 ${meter.is_reading_due ? 'text-status-partial-text' : 'text-muted'}`}
          >
            {meter.is_reading_due && (
              <span
                aria-hidden="true"
                data-testid={`energy-meter-due-${meter.id}`}
                className="bg-status-partial size-1.5 shrink-0 rounded-full"
              />
            )}
            {meter.status_display}
          </span>
        </span>
      </Link>
    </li>
  )
}
